/**
 * Admin: Blackout / Non-Operating Dates.
 *
 * Structure mirrors the Violations tab — a top-level scope switcher
 * (Global / State / Big-City / Region) so an admin can carve out weather-day
 * or holiday closures with the exact geographic precision they need.
 *
 * Global blackouts fan out to every tenant/PM/staff in the org; scoped
 * blackouts only text the audience matching the target geography.
 *
 * Also includes a one-tap "Seed US Federal Holidays" button that inserts
 * the seven fixed non-working days (New Year's Eve/Day, 4th of July, Labor
 * Day, Thanksgiving, Christmas Eve/Day) for the current + next calendar
 * year — idempotent so it doubles as a rolling refresh.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarOff, Trash2, Star, Globe, MapPin, Building2, Layers, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Full US 50 states + DC + PR — Twilio A2P covers all US destinations, and
// blackouts (weather, holidays) can be scoped to any of them. Kept alphabetized
// by name so the picker feels predictable.
const US_STATES = [
  { code: 'AL', name: 'Alabama' },       { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },       { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },    { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },   { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },       { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },        { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },      { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },          { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },      { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },         { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },     { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },      { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },      { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },    { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },{ code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },          { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },        { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },   { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },{ code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },     { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },          { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },      { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// Curated big-city / metro list per state. States without an entry fall back
// to a free-text metro input so admins are never blocked. Focused on the top
// 3-5 metros where valet-trash demand is strongest.
const BIG_CITIES_BY_STATE = {
  AL: ['Birmingham', 'Huntsville', 'Mobile', 'Montgomery'],
  AK: ['Anchorage'],
  AZ: ['Phoenix', 'Tucson', 'Mesa', 'Scottsdale'],
  AR: ['Little Rock', 'Fayetteville'],
  CA: ['Los Angeles', 'San Francisco Bay Area', 'San Diego', 'Sacramento', 'Fresno'],
  CO: ['Denver', 'Colorado Springs', 'Aurora'],
  CT: ['Hartford', 'New Haven', 'Stamford'],
  DE: ['Wilmington', 'Dover'],
  DC: ['Washington D.C.'],
  FL: ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale'],
  GA: ['Atlanta', 'Savannah', 'Augusta'],
  HI: ['Honolulu'],
  ID: ['Boise'],
  IL: ['Chicago', 'Aurora', 'Rockford'],
  IN: ['Indianapolis', 'Fort Wayne'],
  IA: ['Des Moines', 'Cedar Rapids'],
  KS: ['Wichita', 'Kansas City'],
  KY: ['Louisville', 'Lexington'],
  LA: ['New Orleans', 'Baton Rouge', 'Shreveport'],
  ME: ['Portland'],
  MD: ['Baltimore', 'Silver Spring'],
  MA: ['Boston', 'Worcester', 'Springfield'],
  MI: ['Detroit', 'Grand Rapids', 'Ann Arbor'],
  MN: ['Minneapolis-St. Paul'],
  MS: ['Jackson', 'Gulfport'],
  MO: ['Kansas City', 'St. Louis', 'Springfield'],
  MT: ['Billings', 'Missoula'],
  NE: ['Omaha', 'Lincoln'],
  NV: ['Las Vegas', 'Reno'],
  NH: ['Manchester'],
  NJ: ['Newark', 'Jersey City'],
  NM: ['Albuquerque', 'Santa Fe'],
  NY: ['New York City', 'Buffalo', 'Rochester', 'Albany'],
  NC: ['Charlotte', 'Raleigh', 'Greensboro'],
  ND: ['Fargo'],
  OH: ['Columbus', 'Cleveland', 'Cincinnati'],
  OK: ['Oklahoma City', 'Tulsa'],
  OR: ['Portland', 'Eugene'],
  PA: ['Philadelphia', 'Pittsburgh'],
  PR: ['San Juan'],
  RI: ['Providence'],
  SC: ['Charleston', 'Columbia', 'Greenville'],
  SD: ['Sioux Falls'],
  TN: ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga'],
  TX: ['Dallas-Fort Worth', 'Houston', 'Austin', 'San Antonio', 'El Paso'],
  UT: ['Salt Lake City', 'Provo'],
  VT: ['Burlington'],
  VA: ['Virginia Beach', 'Richmond', 'Arlington'],
  WA: ['Seattle', 'Spokane', 'Tacoma'],
  WV: ['Charleston'],
  WI: ['Milwaukee', 'Madison'],
  WY: ['Cheyenne'],
};

const SCOPE_META = {
  global:   { label: 'Global (every market)',       icon: Globe,     ring: 'ring-emerald-500/60' },
  state:    { label: 'State-wide',                  icon: MapPin,    ring: 'ring-sky-500/60'     },
  big_city: { label: 'Big-city metro',              icon: Building2, ring: 'ring-purple-500/60'  },
  region:   { label: 'Single region',               icon: Layers,    ring: 'ring-amber-500/60'   },
};

export default function AdminBlackoutDates({ regions = [] }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Filter (top of card)
  const [filterScope, setFilterScope] = useState('all');
  const [filterState, setFilterState] = useState('');
  const [filterBigCity, setFilterBigCity] = useState('');
  const [filterRegion, setFilterRegion] = useState('');

  // Create form
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('holiday');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('global');
  const [state, setState] = useState('');
  const [bigCity, setBigCity] = useState('');
  const [regionId, setRegionId] = useState('');

  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (filterScope !== 'all') p.set('scope', filterScope);
      if (filterState) p.set('state', filterState);
      if (filterBigCity) p.set('big_city', filterBigCity);
      if (filterRegion) p.set('region_id', filterRegion);
      const res = await fetch(`${API}/api/ops/blackout-dates?${p}`, { credentials: 'include' });
      if (res.ok) setList(await res.json());
    } catch (_) { /* noop */ }
  };

  useEffect(() => { load(); }, [filterScope, filterState, filterBigCity, filterRegion]);

  const create = async () => {
    if (!date) { toast.error('Pick a date'); return; }
    if (scope === 'state' && !state) { toast.error('Pick a state for this scope'); return; }
    if (scope === 'big_city' && !bigCity) { toast.error('Pick a big-city metro'); return; }
    if (scope === 'region' && !regionId) { toast.error('Pick a region'); return; }
    setLoading(true);
    try {
      const body = {
        blackout_date: date, reason, description,
        scope,
        state: scope === 'state' || scope === 'big_city' ? state || undefined : undefined,
        big_city: scope === 'big_city' ? bigCity || undefined : undefined,
        region_id: scope === 'region' ? regionId || undefined : undefined,
      };
      const res = await fetch(`${API}/api/ops/blackout-dates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Added ${data.blackout_date} · matching audience notified.`);
      setDate(''); setDescription('');
      load();
    } finally { setLoading(false); }
  };

  const seedFederalHolidays = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/api/ops/blackout-dates/seed-federal-holidays`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Seed failed'); return; }
      toast.success(data.message || 'US federal holidays seeded.');
      load();
    } finally { setSeeding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this blackout date?')) return;
    try {
      const res = await fetch(`${API}/api/ops/blackout-dates/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) { toast.success('Removed'); load(); }
    } catch (_) { /* noop */ }
  };

  const grouped = useMemo(() => {
    const buckets = { global: [], state: [], big_city: [], region: [] };
    for (const b of list) {
      const key = b.scope || 'global';
      (buckets[key] || buckets.global).push(b);
    }
    return buckets;
  }, [list]);

  return (
    <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6 space-y-5" data-testid="admin-blackout-card">
      {/* Header + federal holiday quick-seed */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <CalendarOff className="h-6 w-6 text-[#FFD60A]" /> NON-OPERATING DATES
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Weather closures, holidays, and other service suspensions — scoped globally, by state,
            big-city metro, or a single region. Adding a date auto-notifies the matching audience.
          </p>
        </div>
        <Button
          onClick={seedFederalHolidays}
          disabled={seeding}
          className="bg-red-600 hover:bg-red-700 text-white font-bold"
          data-testid="admin-blackout-seed-federal-btn"
          title="Insert New Year's Eve/Day, 4th of July, Labor Day, Thanksgiving, Christmas Eve/Day for this year + next."
        >
          <PartyPopper className="h-4 w-4 mr-2" />
          {seeding ? 'Seeding…' : 'Seed US Federal Holidays'}
        </Button>
      </div>

      {/* Scope switcher (mirrors Violations UX) */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Filter by scope</div>
        <div className="flex gap-1 flex-wrap" data-testid="admin-blackout-scope-filter">
          {[['all', 'All'], ...Object.entries(SCOPE_META).map(([k, m]) => [k, m.label])].map(([k, label]) => (
            <button
              key={k}
              onClick={() => { setFilterScope(k); if (k !== 'state' && k !== 'big_city') setFilterBigCity(''); if (k === 'global' || k === 'all') { setFilterState(''); setFilterRegion(''); } }}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition
                ${filterScope === k ? 'bg-[#FFD60A] text-black border-[#FFD60A]' : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700'}`}
              data-testid={`admin-blackout-scope-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Contextual filter selects */}
        {(filterScope === 'state' || filterScope === 'big_city') && (
          <div className="mt-2 flex gap-2 flex-wrap">
            <Select value={filterState || 'ALL'} onValueChange={(v) => { setFilterState(v === 'ALL' ? '' : v); setFilterBigCity(''); }}>
              <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700 text-white" data-testid="admin-blackout-filter-state">
                <SelectValue placeholder="Any state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Any state</SelectItem>
                {US_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>)}
              </SelectContent>
            </Select>
            {filterScope === 'big_city' && filterState && (
              <Select value={filterBigCity || 'ALL'} onValueChange={(v) => setFilterBigCity(v === 'ALL' ? '' : v)}>
                <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700 text-white" data-testid="admin-blackout-filter-bigcity">
                  <SelectValue placeholder="Any metro" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Any metro</SelectItem>
                  {(BIG_CITIES_BY_STATE[filterState] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        {filterScope === 'region' && (
          <div className="mt-2">
            <Select value={filterRegion || 'ALL'} onValueChange={(v) => setFilterRegion(v === 'ALL' ? '' : v)}>
              <SelectTrigger className="w-[280px] bg-zinc-800 border-zinc-700 text-white" data-testid="admin-blackout-filter-region">
                <SelectValue placeholder="Any region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Any region</SelectItem>
                {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id}>{r.region_code} · {r.region_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Create form */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Add a new blackout</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white md:col-span-1"
            data-testid="admin-blackout-date-input"
          />
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white md:col-span-1" data-testid="admin-blackout-reason-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="holiday">Holiday</SelectItem>
              <SelectItem value="weather">Weather</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select value={scope} onValueChange={(v) => { setScope(v); if (v === 'global') { setState(''); setBigCity(''); setRegionId(''); } }}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white md:col-span-1" data-testid="admin-blackout-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SCOPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(scope === 'state' || scope === 'big_city') && (
            <Select value={state} onValueChange={(v) => { setState(v); setBigCity(''); }}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white md:col-span-1" data-testid="admin-blackout-state-select">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {scope === 'big_city' && state && (
            <Select value={bigCity} onValueChange={setBigCity}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white md:col-span-1" data-testid="admin-blackout-bigcity-select">
                <SelectValue placeholder="Metro" />
              </SelectTrigger>
              <SelectContent>
                {(BIG_CITIES_BY_STATE[state] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {scope === 'region' && (
            <Select value={regionId} onValueChange={setRegionId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white md:col-span-2" data-testid="admin-blackout-region-select">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id}>{r.region_code} · {r.region_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input
            placeholder="Reason / description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white md:col-span-2"
            data-testid="admin-blackout-desc-input"
          />
          <Button
            onClick={create}
            disabled={loading || !date}
            className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold md:col-span-1"
            data-testid="admin-blackout-add-btn"
          >
            Add & Notify
          </Button>
        </div>
      </div>

      {/* Grouped list */}
      {list.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-6">No upcoming blackout dates match your filter.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-blackout-list">
          {(['global', 'state', 'big_city', 'region']).map((s) => {
            const rows = grouped[s] || [];
            if (rows.length === 0) return null;
            const meta = SCOPE_META[s];
            const Icon = meta.icon;
            return (
              <div key={s} className={`rounded-lg border border-zinc-800 bg-zinc-950 p-3 ring-1 ${meta.ring}`}>
                <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5" />
                  {meta.label} <span className="ml-1 text-zinc-500">({rows.length})</span>
                </div>
                <ul className="space-y-2">
                  {rows.map((b) => (
                    <li
                      key={b.blackout_id}
                      className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded-lg p-3"
                      data-testid={`admin-blackout-row-${b.blackout_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
                          {new Date(b.blackout_date + 'T00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          {b.is_federal_holiday && (
                            <Badge className="bg-red-600 text-white text-[10px]">
                              <Star className="h-2.5 w-2.5 mr-1" /> Federal
                            </Badge>
                          )}
                          {b.state && <Badge className="bg-sky-700 text-white text-[10px]">{b.state}</Badge>}
                          {b.big_city && <Badge className="bg-purple-700 text-white text-[10px]">{b.big_city}</Badge>}
                          {b.region_id && <Badge className="bg-amber-700 text-white text-[10px]">{b.region_id.slice(0, 8)}</Badge>}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          <Badge className="bg-amber-600 text-black mr-2 capitalize">{b.reason}</Badge>
                          {b.description}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(b.blackout_id)}
                        className="border-red-500 text-red-400 hover:bg-red-500/10 flex-shrink-0"
                        data-testid={`admin-blackout-remove-${b.blackout_id}`}
                        title="Remove blackout"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
